import React, { useState } from 'react';
import { IconButton, TableCell } from '@mui/material';
import { toast } from 'react-toastify';
import {
  Close as CloseIcon,
  Replay as ReplayIcon,
  Repeat as RepeatIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';

// API
import { integrationQueueApi } from '@/main/api';

// Services
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { StatusIntegrationQueueEnum } from '@/main/services/enum/StatusIntegrationQueueEnum';

// Models
import { IntegrationQueueResponse } from '@/main/models/response';
import { IntegrationQueueRequest } from '@/main/models/request';

// Components
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import StatusIntegrationQueue from '@/main/components/shared/status/statusIntegrationQueue';
import { SpinnerLoading } from '@/main/components/shared/styles';

import * as S from './styles';
import { downloadFile } from '@/main/services/utilsFile';

interface IRowDataIntegrationProps {
  integrationQueue: IntegrationQueueResponse;
  getDataIntegrationQueue: (
    filters?: Partial<IntegrationQueueRequest>,
    isResetSearch?: boolean
  ) => void;
}

function RowDataIntegration({
  integrationQueue,
  getDataIntegrationQueue
}: IRowDataIntegrationProps) {
  const [isLoadingDeleteQueue, setIsLoadingDeleteQueue] = useState(false);
  const [isLoadingResendQueue, setIsLoadingResendQueue] = useState(false);
  const [isLoadingdAddBackToQueue, setIsLoadingAddBackToQueue] = useState(
    false
  );

  function handleDeleteQueue(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingDeleteQueue(true);
    integrationQueueApi
      .updateQueueStatus(integrationQueue.guidIntegrationQueue)
      .then(() => {
        toast.success(
          getTranslateByKey('information.integrationQueueDiscardedSuccessfully')
        );
        getDataIntegrationQueue({}, false);
      })
      .finally(() => {
        setIsLoadingDeleteQueue(false);
      });
  }

  function handleResendQueue(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingResendQueue(true);

    integrationQueueApi
      .resend(integrationQueue.guidIntegrationQueue)
      .then(() => {
        toast.success(
          getTranslateByKey('information.integrationQueueResentSuccessfully')
        );
      })
      .finally(() => {
        getDataIntegrationQueue({}, false);
        setIsLoadingResendQueue(false);
      });
  }

  function handleAddBackToQueue(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingAddBackToQueue(true);

    integrationQueueApi
      .addBackToQueue(integrationQueue.guidIntegrationQueue)
      .then(() => {
        toast.success(
          getTranslateByKey(
            'information.integrationQueueAddBackToQueueSuccessfully'
          )
        );
      })
      .finally(() => {
        getDataIntegrationQueue({}, false);
        setIsLoadingAddBackToQueue(false);
      });
  }

  function handleDownload(e: React.MouseEvent<HTMLButtonElement, MouseEvent>){
    e.preventDefault();
    e.stopPropagation();

    if(!isNullUndefined(integrationQueue.file)){
      downloadFile(integrationQueue.file, "request.json", "application/json")
    }
  }

  const disabledDeleteQueue =
    integrationQueue.idStatus === StatusIntegrationQueueEnum.Discarded ||
    integrationQueue.idStatus === StatusIntegrationQueueEnum.Success ||
    isLoadingDeleteQueue ||
    isLoadingResendQueue ||
    isLoadingdAddBackToQueue;
  const disabledResendQueue =
    integrationQueue.idStatus === StatusIntegrationQueueEnum.Discarded ||
    integrationQueue.idStatus === StatusIntegrationQueueEnum.Success ||
    isLoadingDeleteQueue ||
    isLoadingResendQueue ||
    isLoadingdAddBackToQueue;

  const disabledAddBackToQueue =
    integrationQueue.idStatus != StatusIntegrationQueueEnum.Discarded ||
    isLoadingDeleteQueue ||
    isLoadingResendQueue ||
    isLoadingdAddBackToQueue;

  return (
    <S.Container $notClickable>

      <TableCell align="left" width={150}>
        {integrationQueue.createdDateFormatted}
      </TableCell>
      <TableCell>{integrationQueue.claimNumber}</TableCell>
      <TableCell>{integrationQueue.insurerName}</TableCell>
      <TableCell>{integrationQueue.bodyshopName}</TableCell>
      <TableCell>{integrationQueue.integration}</TableCell>
      <TableCell>
        {!isNullUndefined(integrationQueue.errorDescription) &&
        integrationQueue.errorDescription != '' ? (
          <TooltipComponent title={integrationQueue.errorDescription}>
            <S.TableCellErrorDescription>
              {integrationQueue.errorDescription}
            </S.TableCellErrorDescription>
          </TooltipComponent>
        ) : (
          integrationQueue.errorDescription
        )}
      </TableCell>
      <TableCell align="center">{integrationQueue.attempt}</TableCell>

      <TableCell align="center">
        <StatusIntegrationQueue
          idStatus={integrationQueue.idStatus}
          name={integrationQueue.statusName}
        />
      </TableCell>
      <TableCell width={140}>
        <IconButton
          id={`id-integrationQueue-icon-resendQueue-${integrationQueue.guidIntegrationQueue}`}
          title={getTranslateByKey('information.resendIntegrationQueue')}
          onClick={handleResendQueue}
          disabled={disabledResendQueue}>
          {isLoadingResendQueue ? <SpinnerLoading /> : <ReplayIcon />}
        </IconButton>
        <IconButton
          id={`id-integrationQueue-icon-deleteQueue-${integrationQueue.guidIntegrationQueue}`}
          title={getTranslateByKey('information.descartIntegrationQueue')}
          onClick={handleDeleteQueue}
          disabled={disabledDeleteQueue}>
          {isLoadingDeleteQueue ? <SpinnerLoading /> : <CloseIcon />}
        </IconButton>
        <IconButton
          id={`id-integrationQueue-icon-addBackToQueue-${integrationQueue.guidIntegrationQueue}`}
          title={getTranslateByKey('information.addBackIntegrationQueue')}
          onClick={handleAddBackToQueue}
          disabled={disabledAddBackToQueue}>
          {isLoadingdAddBackToQueue ? <SpinnerLoading /> : <RepeatIcon />}
        </IconButton>
        {
          !isNullUndefined(integrationQueue.file) &&

          
        <IconButton
          id={`id-integrationQueue-icon-downloadRequest-${integrationQueue.guidIntegrationQueue}`}
          title={getTranslateByKey('information.downloadFile')}
          onClick={handleDownload}>
           <DownloadIcon />
        </IconButton>
        }
      </TableCell>
    </S.Container>
  );
}

export default RowDataIntegration;
